/**
 * Is a running goal on pace? Teacher's Hedef Görünümü only.
 * Web (dijipo-web-main/…/src/utils/goalPace.ts) and mobile (dijipo-mobile/utils/goalPace.ts)
 * keep byte-for-byte identical copies — change both.
 *
 * Compares how much of the goal is done with how much of its window has gone
 * by. A single-day goal has no "pace" to speak of (it is all today), so it only
 * reports what is left. Dates are the API's calendar days (period_start /
 * period_end, UTC midnights); `todayIso` is the school day.
 */
import { goalPhase, type GoalCardInput } from './goalCard';

export type PaceState = 'behind' | 'on_track' | 'ahead' | 'today' | 'none';

export interface GoalPace {
  state: PaceState;
  /** Whole days left including today (running goals only). */
  daysLeft: number;
  /** Questions still to solve. */
  remaining: number;
  /** Questions a day from today on to still make it. */
  perDay: number;
}

export interface PaceInput extends GoalCardInput {
  period_start?: string | null;
  period_end?: string | null;
  goal_date?: string | null;
}

/** More than this far from the straight line reads as behind / ahead. */
export const PACE_TOLERANCE = 0.15;

const DAY = 86_400_000;
const dayMs = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));

export function goalPace(g: PaceInput, todayIso: string): GoalPace {
  const none: GoalPace = { state: 'none', daysLeft: 0, remaining: 0, perDay: 0 };
  if (goalPhase(g) !== 'running') return none;
  const s = String(g.period_start ?? g.goal_date ?? '').slice(0, 10);
  const e = String(g.period_end ?? g.goal_date ?? '').slice(0, 10);
  const target = g.target_value ?? 0;
  if (!s || !e || target <= 0) return none;

  const achieved = g.achieved_value ?? 0;
  const remaining = Math.max(0, target - achieved);
  const total = Math.round((dayMs(e) - dayMs(s)) / DAY) + 1;
  const today = dayMs(todayIso);
  const daysLeft = Math.max(1, Math.round((dayMs(e) - today) / DAY) + 1);
  const perDay = Math.ceil(remaining / daysLeft);
  if (total <= 1) return { state: 'today', daysLeft: 1, remaining, perDay: remaining };

  // The middle of today counts as elapsed: the student is halfway through it.
  const daysGone = Math.max(0, Math.round((today - dayMs(s)) / DAY));
  const expected = Math.min(1, (daysGone + 0.5) / total);
  const gap = achieved / target - expected;
  const state: PaceState = gap < -PACE_TOLERANCE ? 'behind' : gap > PACE_TOLERANCE ? 'ahead' : 'on_track';
  return { state, daysLeft, remaining, perDay };
}

/** Hex for the web's inline styles; literal Tailwind classes for mobile (NativeWind). */
export const PACE_STYLE: Record<Exclude<PaceState, 'none'>, { label: string; color: string; bg: string; textClass: string; bgClass: string }> = {
  behind: { label: 'Geride', color: '#b42318', bg: '#fdecea', textClass: 'text-[#b42318]', bgClass: 'bg-[#fdecea]' },
  on_track: { label: 'Yolunda', color: '#1f6fd0', bg: '#e8f1fc', textClass: 'text-[#1f6fd0]', bgClass: 'bg-[#e8f1fc]' },
  ahead: { label: 'Önde', color: '#1b7f3a', bg: '#e6f6ea', textClass: 'text-[#1b7f3a]', bgClass: 'bg-[#e6f6ea]' },
  today: { label: 'Bugün son gün', color: '#8a5a00', bg: '#fff3d6', textClass: 'text-[#8a5a00]', bgClass: 'bg-[#fff3d6]' },
};

/** Sort key for the Aktif list: who needs a look first. */
const ORDER: Record<PaceState, number> = { behind: 0, today: 1, on_track: 2, ahead: 3, none: 4 };
export const paceOrder = (p: GoalPace) => ORDER[p.state];

/** "3 gün kaldı · günde 12 soru" — the line under the figure. */
export function paceLine(p: GoalPace): string | null {
  if (p.state === 'none') return null;
  if (p.state === 'today') return `Bugün bitiyor · ${p.remaining} soru kaldı`;
  return `${p.daysLeft} gün kaldı · ${p.remaining} soru${p.remaining > 0 ? ` (günde ~${p.perDay})` : ''}`;
}
